type SessionState = { generation: number; token: string | null; refreshAllowed: boolean }
// A fresh page may bootstrap from its cookie. Explicit session transitions
// suppress refresh until the new login has completed, including an MFA challenge.
let session: SessionState = { generation: 0, token: null, refreshAllowed: true }
// The last cookie writer's credential is private to queued revocation. It may
// outlive the visible session while logout waits for an earlier response.
let cookieAccessToken: string | null = null

export class SessionChangedError extends Error {
  constructor() {
    super('The authentication session changed')
    this.name = 'SessionChangedError'
  }
}

export function getSessionGeneration(): number {
  return session.generation
}

export function assertSessionGeneration(generation: number): void {
  if (generation !== session.generation) throw new SessionChangedError()
}

export function canRefreshAccessToken(): boolean {
  return session.refreshAllowed
}

export function getAccessToken(): string | null {
  return session.token
}

export function setAccessToken(token: string | null | undefined): void {
  session.token = token || null
  cookieAccessToken = session.token
  session.refreshAllowed = Boolean(session.token)
}

export function clearAccessToken(): void {
  session = { generation: session.generation + 1, token: null, refreshAllowed: false }
}

function captureAuthSession() {
  const captured = session
  return {
    generation: captured.generation,
    installToken: (token: string) => {
      // Capture successful cookie writes even when their caller is stale, so
      // the next queued logout revokes that cookie with a valid credential.
      cookieAccessToken = token
      assertSessionGeneration(captured.generation)
      captured.token = token
      captured.refreshAllowed = true
    },
  }
}

export function beginAuthSession() {
  clearAccessToken()
  return captureAuthSession()
}

// Cookie writes cannot be undone by ignoring a stale response. Serialize their
// requests so logout revokes an in-flight refresh/login's actual successor, and
// a subsequent login writes its cookie only after that logout finishes. This
// queue belongs to one page; it does not coordinate cookies across browser tabs.
let mutationTail: Promise<void> = Promise.resolve()

export function queueAuthMutation<T>(generation: number | undefined, mutate: () => Promise<T>): Promise<T> {
  const result = mutationTail.then(async () => {
    if (generation !== undefined) assertSessionGeneration(generation)
    try {
      return await mutate()
    } catch (error) {
      if (generation !== undefined) assertSessionGeneration(generation)
      throw error
    }
  })
  mutationTail = result.then(() => undefined, () => undefined)
  return result
}

export function logoutAuthSession(revoke: (token: string | null, rememberToken: (token: string) => void) => Promise<void>): Promise<void> {
  const capturedToken = getAccessToken()
  clearAccessToken()
  // Logout always runs: a later login waits behind it. Choose the credential
  // after prior responses finish, including a refresh of an expired access token
  // or a login that completed after its caller was already superseded.
  return queueAuthMutation(undefined, async () => {
    await revoke(cookieAccessToken || capturedToken, token => { cookieAccessToken = token })
    cookieAccessToken = null
  })
}

type SessionResponse = { access_token?: string; [key: string]: unknown }
let refreshFlight: { generation: number; promise: Promise<SessionResponse> } | null = null

export function refreshAuthSession(request: (generation: number) => Promise<SessionResponse>): Promise<SessionResponse> {
  if (!canRefreshAccessToken()) return Promise.reject(new SessionChangedError())
  const captured = captureAuthSession()
  if (refreshFlight?.generation === captured.generation) return refreshFlight.promise

  const flight = {
    generation: captured.generation,
    promise: queueAuthMutation(captured.generation, async () => {
      const response = await request(captured.generation)
      if (!response.access_token) throw new Error('Refresh response has no access token')
      captured.installToken(response.access_token)
      return response
    }).finally(() => {
      if (refreshFlight === flight) refreshFlight = null
    }),
  }
  refreshFlight = flight
  return flight.promise
}
